import { useMemo } from 'react';
import { Box, Chip, keyframes } from '@mui/material';
import dayjs from 'dayjs';
import { Moves } from '@gql/base-types';
import MoveTile from '@components/Timeline/Tile/MoveTile';
import { buildEnrichedMove } from '../utils/enrichedMove';

interface ScheduleMoveStatusBadgeProps {
  move: Moves;
  identifier?: string;
  size?: 'compressed' | 'small' | 'medium' | 'large';
  zIndex?: number;
  zoom?: number;
}

// Pulsing animation for active statuses
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
`;

// Helper to determine if move has started (pickup phase or later)
const hasMoveStarted = (status: string | null | undefined): boolean => {
  if (!status) return false;

  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');

  const startedStatuses = [
    'pickup_started',
    'pickup_arrived',
    'pickup_successful',
    'delivery_started',
    'delivery_arrived',
    'delivery_successful',
    'canceled',
    'failed'
  ];

  return startedStatuses.includes(normalizedStatus);
};

// Get badge styling based on status
const getBadgeConfig = (status: string | null | undefined, cancelStatus: string | null | undefined) => {
  if (!status && !cancelStatus) return null;

  const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');

  // Handle cancellation
  if (cancelStatus) {
    return {
      color: '#F44336', // Red
      label: 'CANCELED',
      shouldPulse: false,
      showOverlay: true,
      overlayColor: 'rgba(128, 128, 128, 0.3)',
    };
  }

  // Handle failure
  if (normalizedStatus === 'failed') {
    return {
      color: '#F44336', // Red
      label: 'FAILED',
      shouldPulse: false,
      showOverlay: false,
    };
  }

  // Pickup statuses - Blue
  if (normalizedStatus === 'pickup_started' || normalizedStatus === 'pickup_arrived') {
    return {
      color: '#2196F3', // Blue
      label: status === 'pickup_started' || status === 'pickup started' ? 'PICKUP STARTED' : 'PICKUP ARRIVED',
      shouldPulse: true,
      showOverlay: false,
    };
  }

  if (normalizedStatus === 'pickup_successful') {
    return {
      color: '#2196F3', // Blue
      label: 'PICKUP COMPLETE',
      shouldPulse: false,
      showOverlay: false,
    };
  }

  // Delivery in progress - Green
  if (normalizedStatus === 'delivery_started' || normalizedStatus === 'delivery_arrived') {
    return {
      color: '#4CAF50', // Green
      label: status === 'delivery_started' || status === 'delivery started' ? 'DELIVERY STARTED' : 'DELIVERY ARRIVED',
      shouldPulse: true,
      showOverlay: false,
    };
  }

  // Delivery successful - Teal
  if (normalizedStatus === 'delivery_successful') {
    return {
      color: '#009688', // Teal
      label: 'DELIVERED',
      shouldPulse: false,
      showOverlay: false,
    };
  }

  return null;
};

const ScheduleMoveStatusBadge = ({
  move,
  identifier,
  size = 'medium',
  zIndex = 0,
  zoom = 4,
}: ScheduleMoveStatusBadgeProps) => {
  const status = move?.status;
  const cancelStatus = move?.cancel_status;

  // Calculate enriched move data for delay segments (memoized to avoid recalculating on every render)
  const enriched = useMemo(() => {
    const isToday = dayjs().format('YYYY-MM-DD') === dayjs(move.ready_by).format('YYYY-MM-DD');
    return buildEnrichedMove(move, null, isToday, dayjs());
  }, [move]);

  // Only show badge if move has started
  const showBadge = hasMoveStarted(status) || !!cancelStatus;

  if (!showBadge) {
    // Return MoveTile without badge wrapper but with delay props
    return (
      <MoveTile
        move={move}
        identifier={identifier}
        size={size}
        zIndex={zIndex}
        zoom={zoom}
        pickupDelayMin={enriched.pickupDelayMin}
        driveDelayMin={enriched.driveDelayMin}
        deliveryDelayMin={enriched.deliveryDelayMin}
        isInProgress={enriched.isInProgress}
      />
    );
  }

  const badgeConfig = getBadgeConfig(status, cancelStatus);

  if (!badgeConfig) {
    // Return MoveTile without badge wrapper but with delay props
    return (
      <MoveTile
        move={move}
        identifier={identifier}
        size={size}
        zIndex={zIndex}
        zoom={zoom}
        pickupDelayMin={enriched.pickupDelayMin}
        driveDelayMin={enriched.driveDelayMin}
        deliveryDelayMin={enriched.deliveryDelayMin}
        isInProgress={enriched.isInProgress}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      {/* Optional overlay for canceled moves */}
      {badgeConfig.showOverlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: badgeConfig.overlayColor,
            borderRadius: '8px',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Status badge */}
      <Chip
        label={badgeConfig.label}
        size="small"
        sx={{
          position: 'absolute',
          top: -12,
          left: 8,
          height: 20,
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: badgeConfig.color,
          color: 'white',
          zIndex: 10,
          animation: badgeConfig.shouldPulse ? `${pulse} 2s infinite` : 'none',
          '& .MuiChip-label': {
            px: 1,
          }
        }}
      />

      {/* Move tile */}
      <MoveTile
        move={move}
        identifier={identifier}
        size={size}
        zIndex={zIndex}
        zoom={zoom}
        pickupDelayMin={enriched.pickupDelayMin}
        driveDelayMin={enriched.driveDelayMin}
        deliveryDelayMin={enriched.deliveryDelayMin}
        isInProgress={enriched.isInProgress}
      />
    </Box>
  );
};

export default ScheduleMoveStatusBadge;
